"use client";

import { useState, useRef, useCallback, useEffect } from "react";
import mapboxgl from 'mapbox-gl';

import Map from "../../Map";
import Modal from "./Modal";
import ImageModal from "./ImageModal";
import ProjectHeader from "./ProjectHeader";
import TabContent from "./components/TabContent";
import LoteTab from "./components/LoteTab";
import ProjectPhotosModal from "./components/ProjectPhotosModal";
import AboutProjectModal from "./components/AboutProjectModal";
import OffersModal from "./components/OffersModal";
import DraftProjectModal from "./components/DraftProjectModal";
import ArchivedProjectModal from "./components/ArchivedProjectModal";
import CountryMismatchModal from "./components/CountryMismatchModal";
import LeftSidebar from "./components/LeftSidebar";
import OnboardingOverlay from "./components/OnboardingOverlay";
import { useProject } from "../../contexts/ProjectContext";
import { useCountry } from "../../contexts/CountryContext";
import { getCountryUrl, getCountryKey } from "../../utils/countryUtils";
import {
  getFeaturesInView,
  flyToFeatureAndHighlight,
  highlightLot,
  testHighlighting,
  addSourcesAndLayers,
  addMarkersFromVectorTile,
} from "../../Map/util";
import { removeLotesLayersFromMap } from "../../Map/mapStyles";

import "../../styles.css";

export default function ProjectViewer() {
  const [currentViewData, setCurrentViewData] = useState([]);
  const [activeFeature, setActiveFeature] = useState();
  const mapInstanceRef = useRef();
  const handleLotClickRef = useRef();
  const hasInitialZoomed = useRef(false);
  const selectLotFromMapRef = useRef(); // Ref para seleccionar lote desde el mapa hacia el sidebar
  const [activeTab, setActiveTab] = useState("lote");
  const [selectedLot, setSelectedLot] = useState(null);
  const [isPhotosModalOpen, setIsPhotosModalOpen] = useState(false);
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);
  const [isOffersModalOpen, setIsOffersModalOpen] = useState(false);
  const [isDraftModalOpen, setIsDraftModalOpen] = useState(false);
  const [isArchivedModalOpen, setIsArchivedModalOpen] = useState(false);
  const [isCountryMismatchModalOpen, setIsCountryMismatchModalOpen] = useState(false);
  const [imageModalData, setImageModalData] = useState(null);
  const [mapFullyLoaded, setMapFullyLoaded] = useState(false);
  const [mapStyle, setMapStyle] = useState('satellite'); // 'streets' or 'satellite'

  // Use project context
  const { project, projectId, fetchLotData, loading } = useProject();

  // Use country context
  const { country: currentCountry } = useCountry();
  
  // Define handleLotClick before it's used in useEffect
  const handleLotClick = useCallback((feature) => {

    // Only update tabs for lotes-layer clicks - no modal
    setSelectedLot(feature);
    // Activate the "lote" tab automatically
    setActiveTab("lote");

    // Hacer llamada API para obtener información detallada del lote
    // Intentar obtener el ID del lote desde diferentes propiedades
    const lotId = feature.properties?.id ||
                  feature.properties?.ID ||
                  feature.properties?.objectid ||
                  feature.properties?.OBJECTID ||
                  feature.properties?.FID;


    if (lotId) {
      // Use the context's fetchLotData which now has caching
      fetchLotData(lotId);

      // Highlight the lot on the map (sin hacer zoom)
      if (mapInstanceRef.current) {
        highlightLot(lotId, mapInstanceRef.current);
      } else {
        console.warn('%cProjectViewer: No map instance available for highlighting', 'color: #F44336;');
      }

      // Notify sidebar to select/scroll to this lot
      if (selectLotFromMapRef.current) {
        selectLotFromMapRef.current(lotId);
      }
    } else {
      console.warn('No se pudo encontrar un ID válido para el lote:', feature.properties);
    }

    // Don't set pinned feature or open modal for lotes-layer
  }, [fetchLotData]);

  // Update ref when handleLotClick changes
  useEffect(() => {
    handleLotClickRef.current = handleLotClick;
  }, [handleLotClick]);
  
  // Update document title when project is loaded
  useEffect(() => {
    if (project && project.name) {
      document.title = `${project.name} | lotespro.com - Venta de Lotes`;
    } else {
      document.title = "lotespro.com - Plataforma para Venta de Lotes | Visualización Interactiva";
    }
  }, [project]);

  // Show draft modal when project is in EDICION status
  useEffect(() => {
    if (project && project.status === 'EDICION' && !loading) {
      setIsDraftModalOpen(true);
      setIsArchivedModalOpen(false);
    } else if (project && project.status === 'ARCHIVADO' && !loading) {
      setIsArchivedModalOpen(true);
      setIsDraftModalOpen(false);
    }
  }, [project, loading]);

  // Validate project country matches current subdomain country
  useEffect(() => {
    if (project && currentCountry && !loading) {
      const projectCountryCode = project.country_code;

      // Check if project belongs to a different country
      if (projectCountryCode && projectCountryCode !== currentCountry) {
        setIsCountryMismatchModalOpen(true);
      } else {
        setIsCountryMismatchModalOpen(false);
      }
    }
  }, [project, currentCountry, loading]);
  
  // Debug: verificar projectId

  // Handle lot selection from URL when map is ready
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const lotIdFromUrl = urlParams.get('lote');

    if (!lotIdFromUrl || !mapFullyLoaded || !projectId || !mapInstanceRef.current) {
      return;
    }

    const map = mapInstanceRef.current;

    // Function to try selecting the lot
    const trySelectLot = () => {
      try {
        // Check if the map and layers are ready
        if (!map.isStyleLoaded() || !map.getLayer('lotes-layer-status')) {
          return false;
        }

        // Query the feature to get full data
        const features = map.querySourceFeatures('cloud4geo', {
          sourceLayer: 'lotes_areas',
          filter: ['==', ['get', 'project_id'], projectId]
        });

        // Find the feature matching the lot ID
        const targetFeature = features.find(f => {
          const featureId = f.properties?.id ||
                          f.properties?.ID ||
                          f.properties?.objectid ||
                          f.properties?.OBJECTID ||
                          f.properties?.FID ||
                          f.id;

          return String(featureId) === String(lotIdFromUrl);
        });

        if (targetFeature) {

          // Convert to proper GeoJSON feature
          const convertedFeature = {
            type: 'Feature',
            id: targetFeature.id,
            properties: targetFeature.properties,
            geometry: targetFeature.geometry
          };

          // Use the existing handleLotClick to handle the selection and show popup
          handleLotClick(convertedFeature);

          // Highlight and zoom to the lot when loaded from URL
          const featureId = targetFeature.id ?? (targetFeature.properties?.id || targetFeature.properties?.ID || targetFeature.properties?.objectid);
          if (featureId) {
            // Set feature state to highlight
            map.setFeatureState({
              source: 'cloud4geo',
              sourceLayer: 'lotes_areas',
              id: featureId
            }, {
              selected: true
            });

            // Zoom to the lot when loaded from URL
            if (targetFeature.geometry && targetFeature.geometry.coordinates) {
              try {
                // Calculate bounds from geometry
                const coords = targetFeature.geometry.type === 'Polygon'
                  ? targetFeature.geometry.coordinates[0]
                  : targetFeature.geometry.coordinates[0][0];

                if (coords && coords.length > 0) {
                  const bounds = coords.reduce((bounds, coord) => {
                    return bounds.extend(coord);
                  }, new mapboxgl.LngLatBounds(coords[0], coords[0]));

                  // Fly to the lot with animation
                  map.fitBounds(bounds, {
                    padding: 100,
                    maxZoom: 20,
                    duration: 1500,
                    essential: true
                  });
                }
              } catch (error) {
                console.error('Error zooming to lot from URL:', error);
              }
            }
          }

          return true; // Success
        } else {
          console.warn('Lot not found in map data:', lotIdFromUrl);
          return false;
        }
      } catch (error) {
        console.error('Error trying to select lot from URL:', error);
        return false;
      }
    };

    // Wait for map to be idle (all layers loaded and rendered)
    const onMapIdle = () => {
      const success = trySelectLot();

      if (!success) {
        // If first attempt fails, try again after a short delay
        setTimeout(() => {
          trySelectLot();
        }, 1000);
      }
    };

    // Check if map is already idle
    if (map.loaded()) {
      // Use 'idle' event to ensure all tiles and layers are loaded
      map.once('idle', onMapIdle);
    } else {
      // Wait for 'load' event first, then 'idle'
      map.once('load', () => {
        map.once('idle', onMapIdle);
      });
    }

  }, [mapFullyLoaded, projectId, handleLotClick]);

  // When project data is available and map ready, fit to project bbox (only once on initial load)
  useEffect(() => {
    if (!mapFullyLoaded || !project || !mapInstanceRef.current) return;

    const map = mapInstanceRef.current;

    // Si el proyecto está en EDICION o ARCHIVADO, eliminar lotes si existen y no agregar nuevos
    if (project?.status === 'EDICION' || project?.status === 'ARCHIVADO') {
      try {
        removeLotesLayersFromMap(map);
      } catch (error) {
        // Ignore errors if layers don't exist
      }
      // No agregar lotes ni hacer zoom si está en EDICION o ARCHIVADO
      return;
    }

    // CRITICAL FIX: Wait for projectId to be available before adding layers
    // This prevents showing tiles from all projects when projectId is null/undefined
    if (!projectId) {
      console.warn('ProjectViewer: Waiting for projectId before adding layers...');
      return;
    }

    // Auto zoom only on initial load, not when selecting lots
    if (!hasInitialZoomed.current) {
      hasInitialZoomed.current = true;

      // Update sources, layers and markers ONCE on initial load
      // Now we're guaranteed to have a valid projectId
      addSourcesAndLayers(map, null, handleLotClickRef, projectId, project?.status);
      if (project?.status !== 'EDICION') {
        addMarkersFromVectorTile(map, projectId);
      }

      setTimeout(() => {
        if (mapFullyLoaded && project && mapInstanceRef.current) {
          const bbox = [
            [project.bbox_min_lng, project.bbox_min_lat],
            [project.bbox_max_lng, project.bbox_max_lat]
          ];
          // Globe-to-project fly animation
          mapInstanceRef.current.fitBounds(bbox, {
            padding: 50,
            maxZoom: 18,
            duration: 4000,
            easing: (t) => t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2,
            pitch: 45
          });
        }
      }, 300);
    }
  }, [mapFullyLoaded, project, projectId]);

  // Function to reset view to project bounds
  const handleResetView = useCallback(() => {
    if (!mapInstanceRef.current || !project) return;
    
    const bbox = [
      [project.bbox_min_lng, project.bbox_min_lat],
      [project.bbox_max_lng, project.bbox_max_lat]
    ];
    
    const currentPitch = mapInstanceRef.current.getPitch();
    mapInstanceRef.current.fitBounds(bbox, {
      padding: 50,
      maxZoom: 20,
      duration: 1500,
      easing: (t) => t * (2 - t),
      pitch: currentPitch
    });
  }, [project]);

  const handleMapLoad = (map) => {
    mapInstanceRef.current = map;
    // Delay initial features query until layers are present
    setTimeout(() => {
      setCurrentViewData(getFeaturesInView(map));
    }, 300);

    // Add event listener to update features when map moves/zooms
    map.on("moveend", () => {
      setCurrentViewData(getFeaturesInView(map));
    });

    // Set map as fully loaded after a short delay to ensure layers are ready
    setTimeout(() => {
      setMapFullyLoaded(true);

      // IMPORTANT: Do NOT add layers here - wait for projectId to be available
      // Layers will be added in the useEffect that depends on [mapFullyLoaded, project, projectId]

      // Test highlighting system
      setTimeout(() => {
        testHighlighting(map);
      }, 2000); // Wait 2 seconds after map is ready
    }, 1500); // Wait 1.5 seconds for all layers to be ready
  };

  const handleFeatureClick = (feature) => {
    setActiveFeature(feature);

    // Fly to the feature and highlight it using feature state
    if (mapInstanceRef.current) {
      flyToFeatureAndHighlight(feature, mapInstanceRef.current);
    }
  };

  const handleParcelClick = useCallback((feature) => {
    // Set the selected lot for the tabs
    setSelectedLot(feature);
    // Also open the modal for the feature with project ID
    setActiveFeature({
      ...feature,
      projectId: projectId
    });
  }, [projectId]);


  const handleMapStyleChange = useCallback((newStyle) => {
    const map = mapInstanceRef.current;
    if (!map) return;

    if (newStyle === 'satellite') {
      // Fade in satellite, then hide streets
      map.setLayoutProperty('mapbox-satellite-layer', 'visibility', 'visible');
      map.setPaintProperty('mapbox-satellite-layer', 'raster-opacity', 0);
      setMapStyle('satellite');
      // Animate opacity from 0 to 1
      let opacity = 0;
      const fadeIn = setInterval(() => {
        opacity = Math.min(opacity + 0.1, 1);
        map.setPaintProperty('mapbox-satellite-layer', 'raster-opacity', opacity);
        if (opacity >= 1) {
          clearInterval(fadeIn);
          map.setLayoutProperty('mapbox-streets-layer', 'visibility', 'none');
        }
      }, 30);
    } else {
      // Show streets layer, wait for tiles to load, then crossfade
      map.setLayoutProperty('mapbox-streets-layer', 'visibility', 'visible');
      map.setPaintProperty('mapbox-streets-layer', 'raster-opacity', 0);
      setMapStyle('streets');
      map.once('idle', () => {
        // Streets tiles loaded - fade in streets, fade out satellite
        let opacity = 0;
        const crossfade = setInterval(() => {
          opacity = Math.min(opacity + 0.1, 1);
          map.setPaintProperty('mapbox-streets-layer', 'raster-opacity', opacity);
          map.setPaintProperty('mapbox-satellite-layer', 'raster-opacity', 1 - opacity);
          if (opacity >= 1) {
            clearInterval(crossfade);
            map.setLayoutProperty('mapbox-satellite-layer', 'visibility', 'none');
            map.setPaintProperty('mapbox-satellite-layer', 'raster-opacity', 1);
          }
        }, 30);
      });
    }
  }, []);


  const handleModalClose = () => {
    setActiveFeature(undefined);
  };

  const handleImageModalClose = () => {
    setImageModalData(null);
  };

  // Listen for custom event to open image modal
  useEffect(() => {
    const handleOpenImageModal = (e) => {
      setImageModalData({
        imageUrl: e.detail.imageUrl,
        title: e.detail.markerText
      });
    };

    window.addEventListener('openImageModal', handleOpenImageModal);
    return () => {
      window.removeEventListener('openImageModal', handleOpenImageModal);
    };
  }, []);

  const handleOpenPhotos = () => {
    setIsPhotosModalOpen(true);
  };

  const handleClosePhotos = () => {
    setIsPhotosModalOpen(false);
  };

  const handleOpenAbout = () => {
    setIsAboutModalOpen(true);
  };

  const handleCloseAbout = () => {
    setIsAboutModalOpen(false);
  };

  const handleOpenOffers = () => {
    setIsOffersModalOpen(true);
  };

  const handleCloseOffers = () => {
    setIsOffersModalOpen(false);
  };

  const handleRedirectToCorrectCountry = () => {
    if (project && project.country_code) {
      const projectCountryCode = project.country_code;
      const projectSlug = window.location.pathname.split('/').pop();

      // Get the correct country URL
      const correctCountryUrl = getCountryUrl(projectCountryCode);

      // Redirect to the correct country subdomain with the same project
      window.location.href = `${correctCountryUrl}/proyectos/${projectSlug}`;
    }
  };

  return (
    <>
      {activeFeature && (
        <Modal feature={activeFeature} onClose={handleModalClose} />
      )}
      {imageModalData && (
        <ImageModal 
          imageUrl={imageModalData.imageUrl} 
          title={imageModalData.title}
          onClose={handleImageModalClose} 
        />
      )}
      <ProjectPhotosModal
        isOpen={isPhotosModalOpen}
        onClose={handleClosePhotos}
      />
      <AboutProjectModal
        isOpen={isAboutModalOpen}
        onClose={handleCloseAbout}
      />
      <OffersModal
        isOpen={isOffersModalOpen}
        onClose={handleCloseOffers}
      />
      <DraftProjectModal
        isOpen={isDraftModalOpen}
        onClose={() => setIsDraftModalOpen(false)}
        projectName={project?.name}
      />
      <ArchivedProjectModal
        isOpen={isArchivedModalOpen}
        onClose={() => setIsArchivedModalOpen(false)}
        projectName={project?.name}
      />
      <CountryMismatchModal
        isOpen={isCountryMismatchModalOpen}
        onClose={() => setIsCountryMismatchModalOpen(false)}
        onRedirect={handleRedirectToCorrectCountry}
        projectCountryCode={project?.country_code}
        projectCountry={project?.country}
        currentCountry={currentCountry}
      />
      <main className={`flex flex-col h-screen overflow-hidden ${(isDraftModalOpen || isArchivedModalOpen || isCountryMismatchModalOpen) ? 'pointer-events-none' : ''}`}>
        <div className={(isDraftModalOpen || isArchivedModalOpen || isCountryMismatchModalOpen) ? 'pointer-events-none opacity-50' : ''}>
          <ProjectHeader
            activeTab={activeTab}
            onTabChange={setActiveTab}
            onOpenPhotos={handleOpenPhotos}
            onOpenAbout={handleOpenAbout}
            onOpenOffers={handleOpenOffers}
            onClosePhotos={handleClosePhotos}
            onCloseAbout={handleCloseAbout}
            onCloseOffers={handleCloseOffers}
            onResetView={handleResetView}
          />
        </div>

        {/* Main content area with map and floating panel */}
        <div className={`relative flex-1 ${(isDraftModalOpen || isArchivedModalOpen || isCountryMismatchModalOpen) ? 'pointer-events-none' : ''}`}>
          {/* Map takes full background - only render when projectId is available to ensure tiles are filtered */}
                 <div className="absolute inset-0">
                   {projectId ? (
                   <Map
                     data={currentViewData}
                     onLoad={handleMapLoad}
                     onFeatureClick={handleFeatureClick}
                     onParcelClick={handleParcelClick}
                     onLotClick={handleLotClick}
                     projectId={projectId}
                   />
                   ) : (
                     <div className="h-full w-full flex items-center justify-center bg-gray-100">
                       <div className="text-center">
                         <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-2"></div>
                         <p className="text-gray-500 text-sm">Cargando proyecto...</p>
                       </div>
                     </div>
                   )}
                   
                   {/* Map Style Selector - Desktop only */}
                   <div className="hidden sm:block absolute bottom-4 right-4 z-30">
                     <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                       <button
                         onClick={() => handleMapStyleChange('streets')}
                         className={`px-3 py-2 text-sm font-medium transition-colors ${
                           mapStyle === 'streets' 
                             ? 'bg-green-600 text-white' 
                             : 'bg-white text-gray-700 hover:bg-gray-100'
                         }`}
                         title="🗺️ Vista de calles"
                       >
                         Calles
                       </button>
                       <button
                         onClick={() => handleMapStyleChange('satellite')}
                         className={`px-3 py-2 text-sm font-medium transition-colors ${
                           mapStyle === 'satellite' 
                             ? 'bg-green-600 text-white' 
                             : 'bg-white text-gray-700 hover:bg-gray-100'
                         }`}
                         title="🛰️ Vista satelital"
                       >
                         Satélite
                       </button>
                     </div>
                   </div>
                   
                   {/* Legend - Hidden on mobile */}
                   <div className="hidden md:block absolute bottom-16 left-1/2 transform -translate-x-1/2 bg-white rounded-lg shadow-lg px-4 py-3 z-20">
                     <div className="flex items-center space-x-4">
                       <div className="flex items-center space-x-2">
                         <div className="w-3 h-3 rounded-full bg-blue-400"></div>
                         <span className="text-sm text-gray-700">Disponible</span>
                       </div>
                       <div className="flex items-center space-x-2">
                         <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                         <span className="text-sm text-gray-700">Reservado</span>
                       </div>
                       <div className="flex items-center space-x-2">
                         <div className="w-3 h-3 rounded-full bg-red-500"></div>
                         <span className="text-sm text-gray-700">Vendido</span>
                       </div>
                       <div className="flex items-center space-x-2">
                         <div className="w-3 h-3 rounded-full bg-gray-500"></div>
                         <span className="text-sm text-gray-700">No Disponible</span>
                       </div>
                     </div>
                   </div>

                   {/* Map Controls (below legend) */}
                   <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-20">
                     <div className="flex items-center space-x-2">
                       {/* Home button */}
                       {/* <button
                         onClick={() => {
                           if (mapInstanceRef.current && project) {
                             const bbox = [
                               [project.bbox_min_lng, project.bbox_min_lat],
                               [project.bbox_max_lng, project.bbox_max_lat]
                             ];
                             const currentPitch = mapInstanceRef.current.getPitch();
                             mapInstanceRef.current.fitBounds(bbox, { 
                               padding: 50,
                              //  duration: 20000,
                              duration: 10000,
                                easing: (t) => t * (2 - t),
                                pitch: currentPitch
                             });
                           }
                         }}
                         className="w-11 h-11 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-100 transition-colors"
                         title="🏠 Ir al proyecto - Ajusta la vista para mostrar todo el proyecto (animación lenta)"
                       >
                         <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                           <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                         </svg>
                       </button> */}

                       {/* Navigation buttons - Desktop only */}
                       <button
                         onClick={() => {
                           if (mapInstanceRef.current) {
                             mapInstanceRef.current.panBy([0, -100]);
                           }
                         }}
                         className="hidden sm:flex w-11 h-11 bg-white rounded-full shadow-lg items-center justify-center hover:bg-gray-100 transition-colors"
                         title="⬆️ Mover hacia arriba - Desplaza el mapa hacia el norte"
                       >
                         <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                           <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                         </svg>
                       </button>

                       <button
                         onClick={() => {
                           if (mapInstanceRef.current) {
                             mapInstanceRef.current.panBy([100, 0]);
                           }
                         }}
                         className="hidden sm:flex w-11 h-11 bg-white rounded-full shadow-lg items-center justify-center hover:bg-gray-100 transition-colors"
                         title="➡️ Mover hacia la derecha - Desplaza el mapa hacia el este"
                       >
                         <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                           <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                         </svg>
                       </button>

                       <button
                         onClick={() => {
                           if (mapInstanceRef.current) {
                             mapInstanceRef.current.panBy([-100, 0]);
                           }
                         }}
                         className="hidden sm:flex w-11 h-11 bg-white rounded-full shadow-lg items-center justify-center hover:bg-gray-100 transition-colors"
                         title="⬅️ Mover hacia la izquierda - Desplaza el mapa hacia el oeste"
                       >
                         <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                           <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                         </svg>
                       </button>

                       <button
                         onClick={() => {
                           if (mapInstanceRef.current) {
                             mapInstanceRef.current.panBy([0, 100]);
                           }
                         }}
                         className="hidden sm:flex w-11 h-11 bg-white rounded-full shadow-lg items-center justify-center hover:bg-gray-100 transition-colors"
                         title="⬇️ Mover hacia abajo - Desplaza el mapa hacia el sur"
                       >
                         <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                           <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                         </svg>
                       </button>

                       {/* Zoom buttons */}
                       <button
                         onClick={() => {
                           if (mapInstanceRef.current) {
                             mapInstanceRef.current.zoomIn();
                           }
                         }}
                         className="w-11 h-11 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-100 transition-colors"
                         title="🔍 Acercar - Aumenta el nivel de zoom del mapa"
                       >
                         <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                           <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                         </svg>
                       </button>

                       <button
                         onClick={() => {
                           if (mapInstanceRef.current) {
                             mapInstanceRef.current.zoomOut();
                           }
                         }}
                         className="w-11 h-11 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-100 transition-colors"
                         title="🔍 Alejar - Reduce el nivel de zoom del mapa"
                       >
                         <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                           <path fillRule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                         </svg>
                       </button>

                       {/* Fullscreen button */}
                       <button
                         onClick={() => {
                           if (document.fullscreenElement) {
                             document.exitFullscreen();
                           } else {
                             document.documentElement.requestFullscreen();
                           }
                         }}
                         className="w-11 h-11 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-100 transition-colors"
                         title="📺 Pantalla completa - Activa/desactiva el modo pantalla completa"
                       >
                         <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                           <path fillRule="evenodd" d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 11-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 010-2h4a1 1 0 011 1v4a1 1 0 01-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12zm-9 7a1 1 0 012 0v1.586l2.293-2.293a1 1 0 111.414 1.414L6.414 15H8a1 1 0 010 2H4a1 1 0 01-1-1v-4zm13-1a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 010-2h1.586l-2.293-2.293a1 1 0 111.414-1.414L15.586 13H14a1 1 0 01-1-1z" clipRule="evenodd" />
                         </svg>
                       </button>

                       {/* Bearing control */}
                       <button
                         onClick={() => {
                           if (mapInstanceRef.current) {
                             const currentBearing = mapInstanceRef.current.getBearing();
                             mapInstanceRef.current.setBearing(currentBearing + 15);
                           }
                         }}
                         className="w-11 h-11 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-100 transition-colors"
                         title="🔄 Rotar mapa - Rota la vista del mapa en sentido horario (+15°)"
                       >
                         <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                           <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.293l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clipRule="evenodd" />
                         </svg>
                       </button>

                       {/* Pitch control */}
                       {/* <button
                         onClick={() => {
                           if (mapInstanceRef.current) {
                             const currentPitch = mapInstanceRef.current.getPitch();
                             mapInstanceRef.current.setPitch(Math.min(currentPitch + 15, 85));
                           }
                         }}
                         className="w-11 h-11 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-100 transition-colors"
                         title="📐 Inclinar mapa - Inclina la vista del mapa hacia arriba (+15°)"
                       >
                         <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                           <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                         </svg>
                       </button> */}

                       {/* Reset bearing and pitch */}
                       {/* <button
                         onClick={() => {
                           if (mapInstanceRef.current) {
                             mapInstanceRef.current.setBearing(0);
                             mapInstanceRef.current.setPitch(0);
                           }
                         }}
                         className="w-16 h-11 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-100 transition-colors"
                         title="🔄 Resetear vista - Restaura la rotación e inclinación a valores por defecto"
                       >
                         <span className="text-xs font-semibold text-gray-600">RESET</span>
                       </button> */}
                     </div>
                   </div>
                 </div>

          {/* Left Sidebar - Lots List */}
          <LeftSidebar
            onLotClick={handleLotClick}
            mapInstance={mapInstanceRef.current}
            onSelectLotFromMap={selectLotFromMapRef}
          />

          {/* Right Panel - Lot Details or Project Tab - Desktop only */}
          {selectedLot ? (
            <div className="hidden md:block absolute top-4 right-4 w-72 bg-white rounded-lg shadow-lg z-30">
              <LoteTab selectedLot={selectedLot} onClose={() => setSelectedLot(null)} />
            </div>
          ) : activeTab === 'proyecto' ? (
            <div className="hidden md:block absolute top-4 right-4 w-72 bg-white rounded-lg shadow-lg z-30">
              <TabContent
                activeTab={activeTab}
                currentViewData={currentViewData}
                onOpenPhotos={handleOpenPhotos}
                mapInstance={mapInstanceRef.current}
              />
            </div>
          ) : null}

          {/* Mobile Panel - Lot Details (Bottom) */}
          {selectedLot && (
            <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-lg shadow-2xl z-30 md:hidden max-h-[60vh] overflow-y-auto">
              <LoteTab selectedLot={selectedLot} onClose={() => setSelectedLot(null)} />
            </div>
          )}

        </div>

        {/* Onboarding for first-time users */}
        <OnboardingOverlay />
      </main>
    </>
  );
}

